import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

export type IdentityRole = 'agent' | 'admin';

export interface Identity {
  agentId: string;
  /** 'agent' (default) sees only their own data and the agent filter is locked
   * to themselves. 'admin' sees aggregated data across all agents and can pick
   * any agent via the filter. */
  role: IdentityRole;
  /** Display name for the header. */
  displayName?: string;
  /** Opaque token from the host portal, if any. The dashboard does not use it
   * for GAREM calls (sources handle their own auth) but stores it for future
   * server-side proxies. */
  token?: string;
}

interface IdentityContextValue {
  identity: Identity | null;
  setIdentity: (i: Identity | null) => void;
}

const IdentityContext = createContext<IdentityContextValue | null>(null);

export function IdentityProvider({ initial, children }: { initial: Identity | null; children: ReactNode }) {
  const [identity, setRaw] = useState<Identity | null>(initial);
  const setIdentity = useCallback((i: Identity | null) => setRaw(i), []);
  return <IdentityContext.Provider value={{ identity, setIdentity }}>{children}</IdentityContext.Provider>;
}

export function useIdentity(): IdentityContextValue {
  const ctx = useContext(IdentityContext);
  if (!ctx) throw new Error('useIdentity must be used inside <IdentityProvider>');
  return ctx;
}
